import {Hook} from "./Hook.js";

/**
 * This is a list that fires event when it is changed.
 * @template T
 */
export class ReactiveArray {

    /**
     * This is fired if the list is replaced by new items.
     *
     * @type {Hook}
     */
    filled = new Hook();

    /**
     * This is fired when the list was filled the first time.
     * @type {Hook}
     */
    firstFilled = new Hook();

    /**
     * This is fired when one single item was added. Your listener will receive the added item. It does not mean the
     * array did change its length.
     *
     * @type {Hook}
     */
    pushed = new Hook()

    /**
     * This is fired if the list was extended by many other items. It is fired once after
     * many new items were added. Your listener will receive the offset of the first added item.
     *
     * @type {Hook}
     */
    extended = new Hook();

    /**
     * This is fired when one single item was removed. Your listener will receive the added item. It does not mean the
     * array did change its length.
     *
     * @type {Hook}
     */
    removed = new Hook()

    /**
     * This is fired if the list was cleared. (And is empty)
     *
     * @type {Hook}
     */
    cleared = new Hook();

    /**
     * This is fired if you call updateItem(idx, item)
     *
     * @type {Hook}
     */
    itemUpdated = new Hook();

    /**
     * This is fired if you call replaceItem(idx, item). It fires the old and the new item.
     *
     * @type {Hook}
     */
    itemReplaced = new Hook();

    /**
     * Is fired if the list was resized. It fires the old and new length.
     *
     * @type {Hook}
     */
    resized = new Hook();

    /**
     * This is just a Utility hook you can fire its children. You should fire the item that did change
     *
     * @type {Hook}
     */
    childChanged = new Hook();

    constructor() {
        this._source = []
        this._wasFilled = false;
    }

    /**
     * @return {IterableIterator<T>}
     */
    values() {
        return this._source.values();
    }

    /**
     * @return {IterableIterator<[number, T]>}
     */
    entries() {
        return this._source.entries();
    }

    /**
     * @return {T[]}
     */
    items() {
        return this._source;
    }

    /**
     * @return {?T}
     */
    first() {
        if (this._source.hasOwnProperty(0)) {
            return this._source[0];
        }
        return undefined;
    }

    /**
     * @return {?T}
     */
    last() {
        if (this._source.length === 0) {
            return this.first();
        }
        return this._source.at(this._source.length-1);
    }

    /**
     * @param {function(T)} f
     */
    forEach(f) {
        return this._source.forEach(f);
    }

    /**
     * Fill the array silently, all single operations will not be published.
     *
     * @param {T} items
     */
    fill(...items) {
        this._source = items;
        let oldLength = this._source.length;
        this.filled.fire();
        this.firstFilled.fire();
        this.firstFilled.mute();
        this._fireResize(oldLength, this._source.length);
    }

    /**
     * Push (some) items, every single one is published.
     *
     * @param {T} items
     */
    push(...items) {
        let oldLength = this._source.length;
        items.forEach(item => {
            this._source.push(item);
            this.pushed.fire(item);
        });
        this._fireResize(oldLength, this._source.length);
    }

    /**
     * Silently push many items.
     *
     * @param {T[]} items
     */
    extend(items) {
        let offset = this._source.length;
        this._source.push(...items);
        this.extended.fire(offset);
        this._fireResize(offset, this._source.length);
    }

    /**
     * @return {undefined|T}
     */
    pop() {
        let last = this.last();
        if (!last) {
            return undefined;
        }
        let oldLength = this._source.length;
        let item = this._source.pop();
        this.removed.fire(item);
        this._fireResize(oldLength, this._source.length);
        return item;
    }

    /**
     *
     * @return {undefined|*}
     */
    shift() {
        let first = this.first();
        if (!first) {
            return undefined;
        }
        let oldLength = this._source.length;
        let item = this._source.shift();
        this.removed.fire(item);
        this._fireResize(oldLength, this._source.length);
        return item;
    }


    /**
     * UNTESTED!
     *
     * @param {number} start
     * @param {number} deleteCount
     * @param {T} items
     * @return {T[]}
     */
    splice(start, deleteCount, ...items) {
        if (start < this._source.length) {
            start = start + this._source.length;
        }
        if (start > this._source.length) {
            start = this._source.length;
        }
        let oldLength = this._source.length;
        /** @type {T[]} **/
        let deleted = [];
        for (let i=0; i<deleteCount;i++) {
            let idx = start+1;
            if (idx > this._source.length) {
                continue;
            }

            let previous = this._source.splice(idx, 1, items[idx]);
            if (previous.length) {
                deleted.push(previous[0]);
                this.removed.fire(previous[0]);
            }
            if (items[idx]) {
                this.pushed.fire(items[idx]);
            }
        }
        this._fireResize(oldLength, this._source.length);
        return deleted;

    }

    /**
     * Remove the passed item.
     *
     * @param {T} item
     * @return {number}
     */
    pull(item) {
        const idx = this.indexOf(item);
        if (idx === -1) {
            return -1;
        }
        let oldLength = this._source.length;
        this._source.splice(idx, 1);
        this.removed.fire(item);
        this._fireResize(oldLength, this._source.length);
        return idx;
    }

    /**
     * @param {number} index
     * @returns {?T}
     */
    at(index) {
        return this._source.at(index);
    }

    /**
     * Update an item in a way others will get informed about.
     *
     * @param {number} idx
     * @param {T} item
     */
    update(idx, item) {
        this._source[idx] = item;
        this.itemUpdated.fire(item, idx);
    }

    /**
     * Shortcut to find the index of an item and update it
     * @param {T} obj
     * @param {string|function} compareBy
     * @return {number} The updated index or -1
     */
    updateItem(obj, compareBy='id') {
        let cmp = typeof compareBy === "function" ? compareBy : {compareBy:obj[compareBy]};
        const idx = this.findIndexBy(cmp);
        if (idx !== -1) {
            this.update(idx, obj);
        }
        return idx;
    }

    /**
     * Replace an item in a way others will get informed about. This indicated that the original item was not
     * changed, just at its index another item was inserted.
     *
     * @param {number} idx
     * @param {T} item
     * @return {?T} The replaced item or undefined if none was found
     */
    replace(idx, item) {
        if (!this._source[idx]) {
            return undefined;
        }
        let previous = this._source[idx];
        this._source[idx] = item;
        this.itemReplaced.fire(previous, item);
        this.removed.fire(previous);
        this.pushed.fire(item);
    }

    /**
     * @param {T} item
     * @param {number} fromIndex
     * @return {boolean}
     */
    includes(item, fromIndex=0) {
        return this._source.includes(item, fromIndex);
    }

    /**
     * @param {T} item
     * @param {number} fromIndex
     * @return {number}
     */
    indexOf(item, fromIndex=0) {
        return this._source.indexOf(item, fromIndex);
    }

    /**
     * @param {function(T, number, this)} callbackFn
     * @param thisArg
     * @return {?T}
     */
    find(callbackFn, thisArg=undefined) {
        return this._source.find(callbackFn, thisArg);
    }

    /**
     * @param {function(T, number, this)} callbackFn
     * @param thisArg
     * @return {number}
     */
    findIndex(callbackFn, thisArg=undefined) {
        return this._source.findIndex(callbackFn, thisArg);
    }

    /**
     * Find an index by key=>value array (or function)
     * @param {function|Object} criteria
     */
    findIndexBy(criteria) {
        if (typeof criteria === 'function') {
            return this.findIndex(criteria);
        }
        return this.findIndex((element) => {
            for (let key of Object.keys(criteria)) {
                if (element[key] && element[key] === criteria[key]) {
                    return true;
                }
            }
            return false;
        });
    }

    /**
     * @param {function(T, number, this)} callbackFn
     * @param thisArg
     * @return {T[]}
     */
    filter(callbackFn, thisArg=undefined) {
        return this._source.filter(callbackFn, thisArg);
    }

    /**
     * @param {function(T, number, this)} callbackFn
     * @param thisArg
     * @return {?T[]}
     */
    map(callbackFn, thisArg=undefined) {
        return this._source.map(callbackFn, thisArg);
    }

    /**
     * @return {IterableIterator<number>}
     */
    keys() {
        return this._source.keys();
    }

    clear() {
        let oldLength = this._source.length;
        this._source = [];
        this.cleared.fire();
        this._fireResize(oldLength, 0);
    }

    /**
     * @return {number}
     */
    get length() {
        return this._source.length;
    }

    /**
     * @return {boolean}
     */
    get wasFilled() {
        return this._wasFilled;
    }

    /**
     * @param {number} oldLength
     * @param {number} newLength
     * @private
     */
    _fireResize(oldLength, newLength) {
        if (oldLength !== newLength) {
            this.resized.fire(oldLength, newLength);
        }
    }
}